'use client';

import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useUIStore } from '@/store/uiStore';
import { cn } from '@/lib/utils';

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const styles = {
  success: 'bg-brand-surface border-brand-lime/30 text-brand-lime',
  error: 'bg-brand-surface border-brand-red/30 text-brand-red',
  warning: 'bg-brand-surface border-yellow-500/30 text-yellow-400',
  info: 'bg-brand-surface border-blue-500/30 text-blue-400',
};

function ToastItem({
  id,
  message,
  type,
  duration = 4000,
}: {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}) {
  const { removeToast } = useUIStore();
  const Icon = icons[type];

  useEffect(() => {
    const timer = setTimeout(() => removeToast(id), duration);
    return () => clearTimeout(timer);
  }, [id, duration, removeToast]);

  return (
    <div
      className={cn(
        'flex items-start gap-3 w-full max-w-sm px-4 py-3 rounded-xl border shadow-card',
        'animate-toast-in',
        styles[type]
      )}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <p className="text-sm text-brand-white flex-1">{message}</p>
      <button
        onClick={() => removeToast(id)}
        className="shrink-0 text-brand-gray hover:text-brand-white transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const { toasts } = useUIStore();

  return (
    <div
      className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem {...toast} />
        </div>
      ))}
    </div>
  );
}
